/**
 * Real-Time Notification & Submission Alert Engine for Thinkers Arcade
 * Coordinates incoming alerts from Contact forms, Booking requests,
 * Hosting orders, and Paybill submissions to notify admins in real-time.
 */

export interface SystemNotification {
  id: string;
  type: 'form_submission' | 'hosting_order' | 'booking_request' | 'paybill_payment';
  title: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  details: string;
  amount?: number;
  service?: string;
  timestamp: string;
  read: boolean;
}

const STORAGE_KEY_NOTIFS = 'thinkers_admin_notifications';

// Initial seed if client storage is completely empty
const DEFAULT_NOTIFICATIONS: SystemNotification[] = [
  {
    id: 'seed-notif-1',
    type: 'booking_request',
    title: 'New Strategy Session Diagnostic Booked',
    customerName: 'Kiplangat Rotich',
    customerEmail: 'k.rotich@riftgrain.co.ke',
    customerPhone: '+254 722 418 902',
    service: 'Custom ERP & Inventory Modernization',
    details: 'Looking to migrate 4 agricultural depots in Nakuru from Excel to centralized cloud ERP with KRA eTIMS integration.',
    timestamp: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    read: false,
  },
  {
    id: 'seed-notif-2',
    type: 'hosting_order',
    title: 'LiteSpeed NVMe Pro Cloud Hosting Order',
    customerName: 'Sarah Mwangi',
    customerEmail: 'sarah@apexlaw.co.ke',
    customerPhone: '+254 712 905 441',
    amount: 8875,
    service: 'Business Pro Hosting + .co.ke Domain',
    details: 'Domain: apexlaw.co.ke. Paid via Paybill 542542 with transaction code QJK899120.',
    timestamp: new Date(Date.now() - 1000 * 60 * 65).toISOString(),
    read: false,
  },
  {
    id: 'seed-notif-3',
    type: 'form_submission',
    title: 'New Website Package Quotation Request',
    customerName: 'David Ochieng',
    customerEmail: 'david@lakefrontlogistics.co.ke',
    customerPhone: '+254 733 801 120',
    service: 'Corporate Website Package (KES 18,000)',
    details: 'Client requesting corporate freight portal with tracking inquiry form and WhatsApp integration.',
    timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    read: true,
  },
];

export const getStoredNotifications = (): SystemNotification[] => {
  if (typeof window === 'undefined') return DEFAULT_NOTIFICATIONS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_NOTIFS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // Fallback
  }
  return DEFAULT_NOTIFICATIONS;
};

export const saveStoredNotifications = (notifs: SystemNotification[]): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY_NOTIFS, JSON.stringify(notifs.slice(0, 200)));
  } catch {}
};

/**
 * Fetch fresh notifications from server backend if online, fallback to localStorage
 */
export async function fetchLiveNotifications(): Promise<SystemNotification[]> {
  try {
    const res = await fetch('/api/notifications');
    if (res.ok) {
      const data = await res.json();
      if (data && Array.isArray(data.notifications)) {
        saveStoredNotifications(data.notifications);
        return data.notifications;
      }
    }
  } catch {
    // Fail silently in static or offline preview
  }
  return getStoredNotifications();
}

/**
 * Dispatches a new notification to both the server and local client storage
 */
export async function recordSystemNotification(
  payload: Omit<SystemNotification, 'id' | 'timestamp' | 'read'>
): Promise<SystemNotification> {
  const newNotif: SystemNotification = {
    ...payload,
    id: `notif-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    timestamp: new Date().toISOString(),
    read: false,
  };

  // 1. Save locally
  const current = getStoredNotifications();
  const updated = [newNotif, ...current.slice(0, 199)];
  saveStoredNotifications(updated);

  // 2. Dispatch browser custom event for instant in-tab reactivity
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('thinkers_system_notification', { detail: newNotif }));
  }

  // 3. Post to backend server
  try {
    await fetch('/api/notifications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newNotif),
    });
  } catch {
    // Offline or static fallback
  }

  return newNotif;
}

/**
 * Mark a single notification as read
 */
export async function markNotificationAsRead(id: string): Promise<SystemNotification[]> {
  const current = getStoredNotifications();
  const updated = current.map((n) => (n.id === id ? { ...n, read: true } : n));
  saveStoredNotifications(updated);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('thinkers_system_notification_updated'));
  }

  try {
    await fetch(`/api/notifications/${id}/read`, { method: 'PATCH' });
  } catch {}

  return updated;
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(): Promise<SystemNotification[]> {
  const current = getStoredNotifications();
  const updated = current.map((n) => ({ ...n, read: true }));
  saveStoredNotifications(updated);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('thinkers_system_notification_updated'));
  }

  try {
    await fetch('/api/notifications/mark-all-read', { method: 'POST' });
  } catch {}

  return updated;
}
